/**
 * Client-side ultra-fast image compression utility.
 * Downscales and compresses dealership logo images to tiny, retina-crisp footprints (typically < 20KB)
 * so they load and render in under 5 milliseconds.
 */

const logoCache = new Map();

/**
 * Compresses an image file or base64 data URL to an optimized dimensions & lightweight payload.
 * @param {File|Blob|string} imageSource - File object, Blob, or base64 data URL
 * @param {Object} options - Configuration options
 * @param {number} [options.maxWidth=360] - Maximum target width in pixels
 * @param {number} [options.maxHeight=120] - Maximum target height in pixels
 * @param {number} [options.quality=0.88] - WebP / JPEG quality (0 to 1)
 * @returns {Promise<string>} Resolves with optimized base64 data URL
 */
export async function compressLogoImage(imageSource, options = {}) {
  if (!imageSource) return "";

  const {
    maxWidth = 360,
    maxHeight = 120,
    quality = 0.88
  } = options;

  // Check in-memory cache if string input
  if (typeof imageSource === "string" && logoCache.has(imageSource)) {
    return logoCache.get(imageSource);
  }

  return new Promise((resolve) => {
    const processImage = (src) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      img.onload = () => {
        try {
          let { width, height } = img;

          // If already very small (< 15KB data URL or tiny dimensions), keep as is
          if (typeof imageSource === "string" && imageSource.length < 25000 && width <= maxWidth && height <= maxHeight) {
            logoCache.set(imageSource, imageSource);
            return resolve(imageSource);
          }

          // Calculate new proportional dimensions
          let targetWidth = width;
          let targetHeight = height;

          if (targetWidth > maxWidth) {
            targetHeight = Math.round((targetHeight * maxWidth) / targetWidth);
            targetWidth = maxWidth;
          }

          if (targetHeight > maxHeight) {
            targetWidth = Math.round((targetWidth * maxHeight) / targetHeight);
            targetHeight = maxHeight;
          }

          // Render on canvas
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, targetWidth);
          canvas.height = Math.max(1, targetHeight);

          const ctx = canvas.getContext("2d", { alpha: true });
          if (!ctx) {
            return resolve(src);
          }

          // High-quality bicubic downscaling
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          // Try exporting as WebP (best size/quality with alpha channel), fallback to PNG
          let compressedUrl = "";
          try {
            compressedUrl = canvas.toDataURL("image/webp", quality);
            if (!compressedUrl || compressedUrl.startsWith("data:image/png") || compressedUrl.length < 50) {
              compressedUrl = canvas.toDataURL("image/png");
            }
          } catch (_) {
            compressedUrl = canvas.toDataURL("image/png");
          }

          if (typeof imageSource === "string") {
            logoCache.set(imageSource, compressedUrl);
          }

          resolve(compressedUrl);
        } catch (err) {
          console.warn("[imageCompressor] Error during compression, falling back:", err);
          resolve(src);
        }
      };

      img.onerror = () => {
        resolve(typeof imageSource === "string" ? imageSource : "");
      };

      img.src = src;
    };

    if (imageSource instanceof Blob || imageSource instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        processImage(e.target.result);
      };
      reader.onerror = () => resolve("");
      reader.readAsDataURL(imageSource);
    } else if (typeof imageSource === "string") {
      processImage(imageSource);
    } else {
      resolve("");
    }
  });
}

export default compressLogoImage;
